import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './firebase';
import { User, RoleDocument, TeamAssignment, UserRole } from '../types';
import { User as FirebaseUser } from 'firebase/auth';

export const getUserRole = async (email: string): Promise<UserRole> => {
  try {
    const roleDoc = await getDoc(doc(db, 'roles', email));
    if (roleDoc.exists()) {
      const data = roleDoc.data() as RoleDocument;
      return data.role;
    }
    return 'employee';
  } catch (error) {
    console.error('Error getting user role:', error);
    return 'employee';
  }
};

// Get employee title from team_assignments
export const getEmployeeTitle = async (email: string): Promise<string> => {
  try {
    const q = query(
      collection(db, 'team_assignments'),
      where('employeeEmail', '==', email)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const assignment = snapshot.docs[0].data() as TeamAssignment;
      return assignment.employeeTitle || '';
    }
    return '';
  } catch (error) {
    console.error('Error getting employee title:', error);
    return '';
  }
};

export const createOrUpdateUser = async (firebaseUser: FirebaseUser): Promise<User> => {
  const userRef = doc(db, 'users', firebaseUser.uid);
  const userDoc = await getDoc(userRef);
  const role = await getUserRole(firebaseUser.email || '');
  
  // Always try to get title from team_assignments first (most up-to-date source)
  let title = '';
  if (firebaseUser.email) {
    title = await getEmployeeTitle(firebaseUser.email);
  }
  
  // Fall back to existing title in user doc if team_assignments doesn't have it
  if (!title && userDoc.exists()) {
    title = userDoc.data().title || '';
  }
  
  const userData: Record<string, any> = {
    uid: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName: firebaseUser.displayName || '',
    title,
    role,
    updatedAt: serverTimestamp(),
  };

  // Only add photoURL if it exists (Firestore doesn't allow undefined)
  if (firebaseUser.photoURL) {
    userData.photoURL = firebaseUser.photoURL;
  }

  if (!userDoc.exists()) {
    userData.createdAt = serverTimestamp();
  }

  await setDoc(userRef, userData, { merge: true });
  
  const updatedDoc = await getDoc(userRef);
  return { ...updatedDoc.data(), role } as User;
};

export const getTeamMembers = async (managerEmail: string): Promise<TeamAssignment[]> => {
  try {
    const q = query(
      collection(db, 'team_assignments'),
      where('managerEmail', '==', managerEmail)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as TeamAssignment));
  } catch (error) {
    console.error('Error getting team members:', error);
    return [];
  }
};

/**
 * Recursively gets all team members (direct reports and their reports, etc.)
 * Returns a flat list with a 'level' property indicating depth (1 = direct, 2 = report's report, etc.)
 * 
 * Optimized: Fetches ALL team_assignments in ONE query, then builds tree in memory
 */
export interface TeamMemberWithLevel extends TeamAssignment {
  level: number;
  managerName?: string;
}

export const getTeamMembersRecursive = async (
  managerEmail: string
): Promise<TeamMemberWithLevel[]> => {
  try {
    // Fetch ALL team assignments in one query
    const allAssignments = await getAllTeamAssignments();
    
    // Build the tree in memory
    const buildTree = (
      email: string, 
      level: number, 
      visited: Set<string>
    ): TeamMemberWithLevel[] => {
      if (visited.has(email)) return [];
      visited.add(email);
      
      const directReports = allAssignments.filter(
        a => a.managerEmail === email && a.employeeEmail
      );
      const members: TeamMemberWithLevel[] = [];
      
      for (const member of directReports) {
        if (!member.employeeEmail) continue;
        
        members.push({ 
          ...member, 
          level,
          managerName: level > 1 ? member.managerEmail : undefined
        });
        members.push(...buildTree(member.employeeEmail, level + 1, visited));
      }
      return members;
    };
    
    return buildTree(managerEmail, 1, new Set());
  } catch (error) {
    console.error('Error getting recursive team members:', error);
    return [];
  }
};

/**
 * Gets all email addresses of team members (recursive)
 * Useful for querying goals
 */
export const getAllTeamEmails = async (managerEmail: string): Promise<string[]> => {
  const allMembers = await getTeamMembersRecursive(managerEmail);
  // Filter out any undefined/null emails (bad data in team_assignments)
  return allMembers
    .map(m => m.employeeEmail)
    .filter((email): email is string => !!email);
};

export const getAllManagers = async (): Promise<RoleDocument[]> => {
  try {
    const q = query(
      collection(db, 'roles'),
      where('role', 'in', ['manager', 'super_admin', 'developer'])
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as RoleDocument);
  } catch (error) {
    console.error('Error getting managers:', error);
    return [];
  }
};

export const getManagerForEmployee = async (employeeEmail: string): Promise<string | null> => {
  try {
    const normalizedEmail = employeeEmail.toLowerCase();
    console.log('Searching for employeeEmail:', normalizedEmail);
    
    const q = query(
      collection(db, 'team_assignments'),
      where('employeeEmail', '==', normalizedEmail)
    );
    const snapshot = await getDocs(q);
    console.log('Query results:', snapshot.size, 'documents');
    snapshot.docs.forEach(doc => {
      console.log('Found doc:', doc.id, doc.data());
    });
    
    if (!snapshot.empty) {
      return snapshot.docs[0].data().managerEmail;
    }
    
    // Fallback: try original case (for older records)
    const q2 = query(
      collection(db, 'team_assignments'),
      where('employeeEmail', '==', employeeEmail)
    );
    const snapshot2 = await getDocs(q2);
    if (!snapshot2.empty) {
      return snapshot2.docs[0].data().managerEmail;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting manager:', error);
    return null;
  }
};

export const getAllTeamAssignments = async (): Promise<TeamAssignment[]> => {
  try {
    const snapshot = await getDocs(collection(db, 'team_assignments'));
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as TeamAssignment));
  } catch (error) {
    console.error('Error getting all team assignments:', error);
    return [];
  }
};

/**
 * Updates a team member's title in team_assignments
 */
export const updateTeamMemberTitle = async (
  assignmentId: string, 
  newTitle: string
): Promise<void> => {
  try {
    const assignmentRef = doc(db, 'team_assignments', assignmentId);
    await updateDoc(assignmentRef, {
      employeeTitle: newTitle,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating team member title:', error);
    throw error;
  }
};

/**
 * Toggle canImpersonate permission for a user (developer only)
 */
export const toggleCanImpersonate = async (
  email: string,
  canImpersonate: boolean
): Promise<void> => {
  try {
    const roleRef = doc(db, 'roles', email);
    await updateDoc(roleRef, {
      canImpersonate
    });
  } catch (error) {
    console.error('Error toggling canImpersonate:', error);
    throw error;
  }
};